import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { EditEquipmentDialogComponent } from './dialogs/edit-equipment-dialog/edit-equipment-dialog.component';
import { AtokuEquipment } from '../models/atoku-equipment.model';
import { AtokuService } from '../services/atoku.service';

@Component({
    selector: 'app-atoku-storage-equipment',
    templateUrl: './atoku-storage-equipment.component.html',
    styleUrls: ['./atoku-storage-equipment.component.scss']
})
export class AtokuStorageEquipmentComponent implements OnInit, AfterViewInit {
    displayedColumns: string[] = ['select', 'name', 'total', 'bookedLast30Days'];
    dataSource = new MatTableDataSource<AtokuEquipment>([]);
    currentView: 'equipment' | 'storage' = 'equipment';
    selection = new SelectionModel<AtokuEquipment>(true, []);

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(
        public dialog: MatDialog,
        private atokuService: AtokuService
    ) {
        this.dataSource.filterPredicate = (row, filter) =>
            [
                row.equipmentName,
                String(row.totalAmount ?? ''),
                String(row.bookedLast30Days ?? '')
            ]
                .join(' ')
                .toLowerCase()
                .includes(filter);
    }

    ngOnInit(): void {
        this.loadCurrentView();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
    }

    get hasSelection(): boolean {
        return this.selection.selected.length > 0;
    }

    get hasSingleSelection(): boolean {
        return this.selection.selected.length === 1;
    }

    toggleView(view: 'equipment' | 'storage') {
        this.currentView = view;
        this.loadCurrentView();
        this.selection.clear();
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }
    }

    editSelectedItem(): void {
        const selected = this.selection.selected[0];
        if (!selected) {
            return;
        }

        this.openEditDialog(selected);
    }

    openAddDialog(): void {
        const nextId = this.getNextId(this.dataSource.data);
        const dialogRef = this.dialog.open(EditEquipmentDialogComponent, {
            width: '500px',
            data: {
                id: nextId,
                equipmentName: '',
                totalAmount: 0,
                bookedLast30Days: 0,
                type: this.currentView
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.atokuService.createEquipment({
                    name: result.equipmentName,
                    equipmentName: result.equipmentName,
                    totalAmount: Number(result.totalAmount),
                bookedLast30Days: Number(result.bookedLast30Days),
                type: this.currentView
            }).subscribe(created => {
                this.loadCurrentView();
            });
        }
        });
    }

    openEditDialog(element: AtokuEquipment): void {
        const dialogRef = this.dialog.open(EditEquipmentDialogComponent, {
            width: '500px',
            data: { ...element, type: this.currentView }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.atokuService.updateEquipment(result.id, {
                    name: result.equipmentName,
                    equipmentName: result.equipmentName,
                    totalAmount: Number(result.totalAmount),
                    bookedLast30Days: Number(result.bookedLast30Days),
                    type: this.currentView
                }).subscribe(() => this.loadCurrentView());
            }
        });
    }

    removeSelectedItems(): void {
        const selectedIds = new Set(this.selection.selected.map(item => String(item.id)));
        if (!selectedIds.size) {
            return;
        }

        forkJoin(this.selection.selected.map(item => this.atokuService.deleteEquipment(item.id, this.currentView)))
            .subscribe(() => this.loadCurrentView());
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    toggleRow(item: AtokuEquipment): void {
        this.selection.toggle(item);
    }

    isAllSelected(): boolean {
        const rows = this.dataSource.filteredData;
        return rows.length > 0 && rows.every(row => this.selection.isSelected(row));
    }

    masterToggle(): void {
        const rows = this.dataSource.filteredData;
        if (!rows.length) {
            return;
        }

        if (this.isAllSelected()) {
            rows.forEach(row => this.selection.deselect(row));
            return;
        }

        rows.forEach(row => this.selection.select(row));
    }

    private getNextId(data: AtokuEquipment[]): number {
        const numericIds = data
            .map(item => Number(item.id))
            .filter(id => Number.isFinite(id));
        return numericIds.length ? Math.max(...numericIds) + 1 : 1;
    }

    private loadCurrentView(): void {
        this.atokuService.getEquipment(this.currentView).subscribe(items => {
            this.dataSource.data = items.map(item => {
                const raw = item as unknown as Record<string, unknown>;
                return {
                    id: String(raw['id'] ?? ''),
                    equipmentName: String(raw['equipmentName'] ?? raw['name'] ?? ''),
                    totalAmount: Number(raw['totalAmount'] ?? 0),
                    bookedLast30Days: Number(raw['bookedLast30Days'] ?? 0),
                    price: Number(raw['price'] ?? 0)
                };
            });
            this.selection.clear();
        });
    }
}
